/**
 * Admin V3 — Drivers — Document Viewer (Plan §2.2 M-F-12).
 * Streams document binary via GET /admin/drivers/{driver_id}/documents/{doc_id}/content.
 * Backend: stream_document() with tenant filter + RBAC guard (ADMIN_DRIVER_DOCUMENT_VIEW).
 */
import React, { useEffect, useState } from 'react';
import { X, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { t } from '../../i18n';

const API_URL = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

const DocumentViewer = ({ driverId, document, onClose, locale = 'fr' }) => {
  const { getAuthHeaders } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    (async () => {
      try {
        const headers = getAuthHeaders();
        const res = await fetch(
          `${API_URL}/admin/drivers/${driverId}/documents/${document.id}/content`,
          { headers },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(url);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [driverId, document.id, getAuthHeaders]);

  const isImage = (document.mime_type || '').startsWith('image/');
  const isPdf   = document.mime_type === 'application/pdf';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center" data-testid="document-viewer">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[16px] font-semibold text-slate-900">
            {t(`compliance.doctype.${document.document_type}`, locale)} (v{document.version})
          </h3>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-900 text-sm inline-flex items-center gap-1"
              >
                <ExternalLink className="h-4 w-4" /> {t('compliance.documents.action.view', locale)}
              </a>
            )}
            <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 rounded-lg flex items-center justify-center">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-slate-500" />}
          {!loading && error && <div className="text-rose-700 text-sm">{t('compliance.common.error', locale)}</div>}
          {!loading && !error && blobUrl && isImage && (
            <img src={blobUrl} alt={document.document_type} className="max-w-full max-h-full" />
          )}
          {!loading && !error && blobUrl && isPdf && (
            <iframe src={blobUrl} title={document.document_type} className="w-full h-full" />
          )}
          {!loading && !error && blobUrl && !isImage && !isPdf && (
            <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
              {t('compliance.documents.action.view', locale)}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
